import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Film, MapPin, Calendar, FileVideo, AlertCircle, FileText, Link as LinkIcon, Clock, ShieldCheck, ChevronLeft, Tag, Scale, ThumbsUp, ThumbsDown, AlertTriangle, History } from "lucide-react";
import { api } from "@/lib/api";
import { CommentsSection } from "@/components/social/CommentsSection";
import { VoteButtons } from "@/components/social/VoteButtons";

const RELIABILITY_STYLE = {
    "Government Released":   { color: "#9bb8d4" },
    "Historical Archive":    { color: "#9bb8d4" },
    "Witness Submitted":     { color: "#a8c79d" },
    "Archive Footage":       { color: "#c4d8ea" },
    "Under Investigation":   { color: "#e6c47a" },
    "Unverified Media":      { color: "#e6c47a" },
};

export default function MediaArchiveDetail() {
    const { slug } = useParams();
    const [entry, setEntry] = useState(null);
    const [error, setError] = useState(null);
    const [voteInfo, setVoteInfo] = useState(null);

    useEffect(() => {
        let active = true;
        api.get(`/media-archive/${slug}`)
            .then((r) => active && setEntry(r.data))
            .catch((e) => active && setError(e?.response?.status === 404 ? "not_found" : "load_error"));
        Promise.all([
            api.get("/social/counts", { params: { target_type: "media", ids: slug } }),
            api.get("/social/votes/me", { params: { target_type: "media", ids: slug } }).catch(() => ({ data: { votes: {} } })),
        ]).then(([cRes, mRes]) => {
            if (!active) return;
            const c = cRes.data?.counts?.[slug] || {};
            setVoteInfo({ score: c.vote_score || 0, my: (mRes.data?.votes || {})[slug] || 0 });
        }).catch(() => active && setVoteInfo({ score: 0, my: 0 }));
        return () => { active = false; };
    }, [slug]);

    if (error === "not_found") {
        return (
            <div className="ok-container py-20 text-center" data-testid="media-detail-notfound">
                <h1 className="font-display text-3xl mb-3">Entry not found</h1>
                <Link to="/media-archive" className="ok-btn-outline mt-6">Back to Media Archive</Link>
            </div>
        );
    }
    if (!entry) {
        return <div className="ok-container py-20"><div className="ok-card animate-pulse h-96" style={{ background: "var(--ok-surface)" }} /></div>;
    }

    return (
        <div className="ok-container py-8 lg:py-12" data-testid="media-detail-page">
            <Link to="/media-archive" className="font-mono text-[10px] uppercase tracking-[0.3em] opacity-60 hover:opacity-100 flex items-center gap-1 mb-6">
                <ChevronLeft className="w-3 h-3" /> Media Archive
            </Link>

            {/* HEADER */}
            <header className="mb-10">
                <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-[var(--ok-accent)] mb-3 flex flex-wrap items-center gap-3" data-testid="media-detail-classifications">
                    <Film className="w-3 h-3" />
                    <span>{entry.classification}</span>
                    {entry.secondary_classification && (
                        <>
                            <span className="opacity-40">·</span>
                            <span>{entry.secondary_classification}</span>
                        </>
                    )}
                </div>
                <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] mb-4" data-testid="media-detail-title">
                    {entry.title}
                </h1>
                <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs sm:text-sm opacity-80 font-mono mb-5">
                    {entry.date && <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-[var(--ok-accent)]" />{entry.date}</span>}
                    {entry.location && <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[var(--ok-accent)]" />{entry.location}</span>}
                </div>

                {/* Community vote on this media entry */}
                {voteInfo && (
                    <div className="ok-card inline-flex items-center gap-3 px-4 py-2 mb-5" data-testid="media-vote-row">
                        <VoteButtons
                            targetType="media"
                            targetId={slug}
                            initialScore={voteInfo.score}
                            initialMyVote={voteInfo.my}
                            layout="horizontal"
                            size="lg"
                        />
                        <span className="font-mono text-[10px] uppercase tracking-[0.3em] opacity-60">Community assessment</span>
                    </div>
                )}

                {/* Prominent reliability + status banner */}
                {(entry.reliability || entry.status) && (
                    <div className="ok-card p-4 flex flex-wrap items-center gap-4 mb-5" data-testid="media-detail-reliability-banner" style={{ background: "var(--ok-surface)", borderLeft: `3px solid ${RELIABILITY_STYLE[entry.reliability]?.color || "var(--ok-accent)"}` }}>
                        {entry.reliability && (
                            <div className="flex items-center gap-2">
                                <Scale className="w-4 h-4" style={{ color: RELIABILITY_STYLE[entry.reliability]?.color || "var(--ok-accent)" }} />
                                <div>
                                    <div className="font-mono text-[9px] uppercase tracking-[0.3em] opacity-60">Reliability tier</div>
                                    <div className="font-mono text-xs uppercase tracking-[0.25em] font-bold" style={{ color: RELIABILITY_STYLE[entry.reliability]?.color || "var(--ok-accent)" }}>
                                        {entry.reliability}
                                    </div>
                                </div>
                            </div>
                        )}
                        {entry.status && (
                            <div className="flex items-center gap-2 pl-4 border-l" style={{ borderColor: "var(--ok-border)" }}>
                                <AlertCircle className="w-4 h-4" style={{ color: "#e6c47a" }} />
                                <div>
                                    <div className="font-mono text-[9px] uppercase tracking-[0.3em] opacity-60">Status</div>
                                    <div className="font-mono text-xs uppercase tracking-[0.25em] font-bold" style={{ color: "#e6c47a" }}>{entry.status}</div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {entry.summary && (
                    <p className="max-w-3xl text-sm sm:text-base opacity-85 leading-relaxed">{entry.summary}</p>
                )}

                {/* Link back to the corresponding case page, when available */}
                {entry.linked_case_id && (
                    <Link
                        to={`/encounters/${entry.linked_case_id}`}
                        className="ok-card ok-card-hover p-4 mt-5 inline-flex items-center gap-3 group max-w-3xl"
                        data-testid="media-detail-case-link"
                    >
                        <FileText className="w-4 h-4 text-[var(--ok-accent)] flex-shrink-0" />
                        <div className="flex-1">
                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ok-accent)]">Linked case file</div>
                            <div className="text-sm font-semibold mt-0.5">{entry.linked_case_title || "View the full case page"}</div>
                        </div>
                        <ChevronLeft className="w-4 h-4 rotate-180 opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition" />
                    </Link>
                )}

                {entry.indopacom_note && (
                    <div className="ok-card p-4 mt-5 max-w-3xl" data-testid="media-detail-indopacom-note" style={{ background: "var(--ok-surface)", borderLeft: "3px solid var(--ok-accent)" }}>
                        <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[var(--ok-accent)] mb-1.5">INDOPACOM context</div>
                        <p className="text-xs sm:text-sm leading-relaxed opacity-90">{entry.indopacom_note}</p>
                    </div>
                )}

                {/* Neutrality notice — shown for Unverified Media */}
                {entry.reliability === "Unverified Media" && (
                    <div className="ok-card p-4 mt-5 flex items-start gap-3 max-w-3xl" data-testid="media-detail-neutrality-notice" style={{ background: "rgba(230,196,122,0.06)", borderColor: "rgba(230,196,122,0.35)" }}>
                        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "#e6c47a" }} />
                        <div className="text-xs sm:text-sm leading-relaxed opacity-90">
                            This archive records the existence of the material and the discussion around it. It <strong>does not assert</strong> that the footage is genuine, and <strong>does not assert</strong> that the footage is fabricated. Visitors are invited to review the evidence and the arguments and form their own conclusions.
                        </div>
                    </div>
                )}
            </header>

            <div className="grid lg:grid-cols-12 gap-10">
                <main className="lg:col-span-8 space-y-12">
                    {/* SECTION 1 · OVERVIEW */}
                    <Section eyebrow="Section 01" title="Overview" testid="section-overview">
                        {entry.overview ? <p className="text-sm sm:text-base opacity-90 leading-relaxed whitespace-pre-line">{entry.overview}</p> : <Empty>No overview provided.</Empty>}
                    </Section>

                    {/* SECTION · SOURCE HISTORY (Unverified-Media entries) */}
                    {entry.source_history && (
                        <Section eyebrow="Section 02" title="Source History" testid="section-source-history">
                            <div className="flex items-start gap-3">
                                <History className="w-4 h-4 mt-1 text-[var(--ok-accent)] flex-shrink-0" />
                                <p className="text-sm sm:text-base opacity-90 leading-relaxed">{entry.source_history}</p>
                            </div>
                        </Section>
                    )}

                    {/* SECTION · CLAIMS ASSOCIATED */}
                    {(entry.claims_associated || []).length > 0 && (
                        <Section eyebrow="Section 03" title="Claims Associated With Media" testid="section-claims">
                            <ul className="space-y-2.5">
                                {entry.claims_associated.map((c, i) => (
                                    <li key={i} className="flex gap-3 text-sm opacity-90 leading-relaxed">
                                        <Tag className="w-3.5 h-3.5 mt-1 text-[var(--ok-accent)] flex-shrink-0" />
                                        <span>{c}</span>
                                    </li>
                                ))}
                            </ul>
                        </Section>
                    )}

                    {/* SECTION · ARGUMENTS FOR / AGAINST (paired, neutral framing) */}
                    {((entry.arguments_for || []).length > 0 || (entry.arguments_against || []).length > 0) && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="section-arguments">
                            <Section eyebrow="Section 04" title="Supporting Authenticity" testid="section-arguments-for">
                                {(entry.arguments_for || []).length > 0 ? (
                                    <ul className="space-y-2.5">
                                        {entry.arguments_for.map((a, i) => (
                                            <li key={i} className="flex gap-3 text-sm opacity-90 leading-relaxed">
                                                <ThumbsUp className="w-3.5 h-3.5 mt-1 flex-shrink-0" style={{ color: "#a8c79d" }} />
                                                <span>{a}</span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : <Empty>None catalogued.</Empty>}
                            </Section>
                            <Section eyebrow="Section 05" title="Against Authenticity" testid="section-arguments-against">
                                {(entry.arguments_against || []).length > 0 ? (
                                    <ul className="space-y-2.5">
                                        {entry.arguments_against.map((a, i) => (
                                            <li key={i} className="flex gap-3 text-sm opacity-90 leading-relaxed">
                                                <ThumbsDown className="w-3.5 h-3.5 mt-1 flex-shrink-0" style={{ color: "#e6c47a" }} />
                                                <span>{a}</span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : <Empty>None catalogued.</Empty>}
                            </Section>
                        </div>
                    )}

                    {/* SECTION · TIMELINE (only if present and we don't already have source_history sections) */}
                    {!entry.source_history && (entry.timeline || []).length > 0 && (
                        <Section eyebrow="Section 02" title="Timeline" testid="section-timeline">
                            <ol className="space-y-4 relative pl-6 border-l" style={{ borderColor: "var(--ok-border)" }}>
                                {entry.timeline.map((t, i) => (
                                    <li key={i} className="relative">
                                        <span className="absolute -left-[1.4rem] top-1.5 w-2 h-2 rounded-full" style={{ background: "var(--ok-accent)" }} />
                                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ok-accent)] mb-1 flex items-center gap-1.5">
                                            <Clock className="w-3 h-3" /> {t.t}
                                        </div>
                                        <p className="text-sm opacity-90 leading-relaxed">{t.v}</p>
                                    </li>
                                ))}
                            </ol>
                        </Section>
                    )}

                    {/* SECTION · KNOWN FACTS (kept for non-unverified entries) */}
                    {!entry.source_history && (entry.known_facts || []).length > 0 && (
                        <Section eyebrow="Section 03" title="Known Facts" testid="section-known-facts">
                            <ul className="space-y-2.5">
                                {entry.known_facts.map((f, i) => (
                                    <li key={i} className="flex gap-3 text-sm opacity-90 leading-relaxed">
                                        <ShieldCheck className="w-3.5 h-3.5 mt-1 text-[var(--ok-accent)] flex-shrink-0" />
                                        <span>{f}</span>
                                    </li>
                                ))}
                            </ul>
                        </Section>
                    )}

                    {/* SECTION · MEDIA EVIDENCE */}
                    <Section eyebrow="Section 06" title="Media Evidence" testid="section-media">
                        {(entry.media || []).length > 0 ? (
                            <div className="space-y-6">
                                {entry.media.map((m, i) => <MediaPanel key={i} m={m} />)}
                            </div>
                        ) : <Empty>No media attached.</Empty>}
                    </Section>

                    {/* SECTION · RELATED (cases, species, encounters, documents, witness reports) */}
                    {((entry.related_cases || []).length > 0 || (entry.related_species || []).length > 0 || (entry.related_encounters || []).length > 0 || (entry.related_documents || []).length > 0 || (entry.related_witness_reports || []).length > 0) && (
                        <Section eyebrow="Section 07" title="Related Cases" testid="section-related">
                            <div className="space-y-5">
                                {(entry.related_cases || []).length > 0 && (
                                    <RelatedGroup label="Historical cases" items={entry.related_cases.map((c) => ({ label: c }))} />
                                )}
                                {(entry.related_species || []).length > 0 && (
                                    <RelatedGroup label="Species / entities" items={entry.related_species.map((s) => ({ label: s.replace(/-/g, " "), href: `/species/${s}` }))} />
                                )}
                                {(entry.related_encounters || []).length > 0 && (
                                    <RelatedGroup label="Encounters" items={entry.related_encounters.map((id) => ({ label: id, href: `/encounters/${id}` }))} />
                                )}
                                {(entry.related_documents || []).length > 0 && (
                                    <RelatedGroup label="Government documents" items={entry.related_documents.map((d) => ({ label: d.label || d, href: d.url }))} />
                                )}
                                {(entry.related_witness_reports || []).length > 0 && (
                                    <RelatedGroup label="Witness reports" items={entry.related_witness_reports.map((w) => ({ label: w.label || w }))} />
                                )}
                            </div>
                        </Section>
                    )}

                    {/* SECTION · SOURCE MATERIAL */}
                    <Section eyebrow="Section 08" title="Source Material" testid="section-sources">
                        {(entry.source_material || []).length > 0 ? (
                            <ul className="space-y-2.5">
                                {entry.source_material.map((s, i) => (
                                    <li key={i}>
                                        <a href={s.url} target="_blank" rel="noopener noreferrer" className="flex items-start gap-2 text-sm hover:text-[var(--ok-accent)] transition-colors group">
                                            <LinkIcon className="w-3.5 h-3.5 mt-1 text-[var(--ok-accent)] flex-shrink-0" />
                                            <span className="underline-offset-4 group-hover:underline">{s.label}</span>
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        ) : <Empty>No external sources catalogued.</Empty>}
                    </Section>

                    {/* SECTION · ARCHIVE NOTES */}
                    <Section eyebrow="Section 09" title="Archive Notes" testid="section-notes">
                        <div className="space-y-3 text-sm opacity-90 leading-relaxed">
                            {entry.archive_notes && <p>{entry.archive_notes}</p>}
                            {entry.evidence_notes && (
                                <div className="ok-card p-4" style={{ background: "var(--ok-surface)" }}>
                                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ok-accent)] mb-1.5 flex items-center gap-1.5">
                                        <FileText className="w-3 h-3" /> Evidence notes
                                    </div>
                                    <p className="opacity-90">{entry.evidence_notes}</p>
                                </div>
                            )}
                            {!entry.archive_notes && !entry.evidence_notes && <Empty>No archive notes recorded.</Empty>}
                        </div>
                    </Section>
                </main>

                {/* SIDEBAR · Metadata */}
                <aside className="lg:col-span-4 lg:sticky lg:top-24 self-start">
                    <div className="ok-card p-5 space-y-5" style={{ background: "var(--ok-glass-bg-strong)", borderColor: "var(--ok-glass-border)" }} data-testid="media-detail-sidebar">
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ok-accent)] flex items-center gap-2 mb-3">
                                <FileText className="w-3 h-3" /> Archive metadata
                            </div>
                            <dl className="space-y-2.5 text-xs">
                                <Row k="Reference" v={entry.internal_id || entry.slug.toUpperCase()} />
                                {entry.reliability && <Row k="Reliability" v={entry.reliability} accent={entry.reliability === "Unverified Media" || entry.reliability === "Under Investigation" ? "warn" : null} />}
                                {entry.status && <Row k="Status" v={entry.status} accent="warn" />}
                                <Row k="Classification" v={entry.classification} />
                                {entry.secondary_classification && <Row k="Secondary" v={entry.secondary_classification} />}
                                <Row k="Date" v={entry.date || "—"} />
                                <Row k="Location" v={entry.location || "—"} />
                                <Row k="Media items" v={String((entry.media || []).length)} />
                                {(entry.media || []).some(m => m.status === "pending") && (
                                    <Row k="Pending" v={String((entry.media || []).filter(m => m.status === "pending").length)} accent="warn" />
                                )}
                            </dl>
                        </div>
                        {(entry.tags || []).length > 0 && (
                            <div>
                                <div className="font-mono text-[10px] uppercase tracking-[0.3em] opacity-60 flex items-center gap-1.5 mb-2">
                                    <Tag className="w-3 h-3" /> Tags
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    {entry.tags.map((t) => (
                                        <span key={t} className="ok-tag" style={{ fontSize: "10px" }}>{t}</span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </aside>
            </div>
            <CommentsSection targetType="media" targetId={entry.slug} />
        </div>
    );
}

function MediaPanel({ m }) {
    const isPending = m.status === "pending" || !m.url;
    const isVideo = m.kind === "video";
    const isImage = m.kind === "image";
    return (
        <div className="ok-card overflow-hidden" data-testid={`media-panel-${m.kind || "file"}`} style={{ background: "var(--ok-image-frame)" }}>
            <div className="relative">
                {isPending ? (
                    <div className="aspect-[16/9] flex flex-col items-center justify-center gap-3" style={{ background: "var(--ok-image-frame)" }}>
                        <AlertCircle className="w-10 h-10 text-[#e6c47a] opacity-80" />
                        <div className="font-mono text-[11px] uppercase tracking-[0.35em] text-[#e6c47a]">
                            {m.pending_note || "Video Pending Upload"}
                        </div>
                        <div className="font-mono text-[9px] uppercase tracking-[0.3em] opacity-60 max-w-xs text-center">
                            Slot reserved. The video file will be attached without modifying this page.
                        </div>
                    </div>
                ) : isVideo ? (
                    <video
                        controls
                        preload="metadata"
                        playsInline
                        className="w-full aspect-video bg-black"
                        src={m.embed_url || m.url}
                        data-testid="media-video"
                    >
                        <source src={m.embed_url || m.url} />
                        Your browser does not support inline video. <a href={m.embed_url || m.url} target="_blank" rel="noopener noreferrer">Download the video</a>.
                    </video>
                ) : isImage ? (
                    <img src={m.embed_url || m.url} alt={m.label || ""} className="w-full aspect-video object-cover" />
                ) : (
                    <a href={m.url} target="_blank" rel="noopener noreferrer" className="block aspect-video flex items-center justify-center text-[var(--ok-accent)] hover:underline">
                        <FileVideo className="w-8 h-8 mr-2" /> Open file
                    </a>
                )}
            </div>
            <div className="px-4 py-3 border-t flex items-start justify-between gap-4" style={{ borderColor: "var(--ok-border)", background: "var(--ok-archival-meta-bg)" }}>
                <div className="flex-1 min-w-0">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em]" style={{ color: isPending ? "#e6c47a" : "#c4d8ea" }}>
                        {(m.kind || "file").toUpperCase()} · {isPending ? "Awaiting upload" : "Available"}
                    </div>
                    {m.label && <div className="text-sm font-semibold mt-1 truncate" style={{ color: "#e6edf3" }}>{m.label}</div>}
                    {m.source && (
                        <div className="text-xs opacity-70 mt-1" style={{ color: "#c4d8ea" }}>
                            Source: {m.source}
                        </div>
                    )}
                </div>
                {m.source_url && (
                    <a href={m.source_url} target="_blank" rel="noopener noreferrer" className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#9bb8d4] hover:underline flex-shrink-0 flex items-center gap-1">
                        Source <LinkIcon className="w-3 h-3" />
                    </a>
                )}
            </div>
        </div>
    );
}

function RelatedGroup({ label, items }) {
    if (!items || items.length === 0) return null;
    return (
        <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] opacity-60 mb-2">{label}</div>
            <div className="flex flex-wrap gap-2">
                {items.map((it, i) => it.href ? (
                    <Link key={i} to={it.href} className="ok-tag hover:opacity-100" style={{ color: "var(--ok-accent)", borderColor: "var(--ok-accent)" }}>
                        {it.label}
                    </Link>
                ) : (
                    <span key={i} className="ok-tag" style={{ color: "var(--ok-accent)", borderColor: "var(--ok-accent)" }}>{it.label}</span>
                ))}
            </div>
        </div>
    );
}

function Section({ eyebrow, title, children, testid }) {
    return (
        <section data-testid={testid}>
            <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-[var(--ok-accent)] mb-2">{eyebrow}</div>
            <h2 className="font-display text-2xl sm:text-3xl mb-5 tracking-tight">{title}</h2>
            {children}
        </section>
    );
}

function Row({ k, v, accent }) {
    return (
        <div className="flex items-start justify-between gap-3">
            <dt className="font-mono text-[10px] uppercase tracking-[0.3em] opacity-60 flex-shrink-0">{k}</dt>
            <dd className="font-display font-bold text-xs text-right break-words" style={{ color: accent === "warn" ? "#e6c47a" : "var(--ok-on-glass)" }}>{v}</dd>
        </div>
    );
}

function Empty({ children }) {
    return <p className="text-sm opacity-50 italic">{children}</p>;
}
